import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useEffect } from "react";
import { useDayClosingSummaryAPI } from "@/app/hooks/api_hooks/dayClosing/useDayClosingSummaryAPI";
import CongratulationsCard from "./CongratulationsCard";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useDayClosingCurrentShift } from "@/app/hooks/api_hooks/dayClosing/useDayClosingCurrentShift";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const DayclosingSummaryComponent = () => {
  const { execute: fetchDayClosingData, closureData } = useDayClosingSummaryAPI();
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();

  const time = queryParams.get("t") ?? "";
  const shiftTiming = queryParams.get("shift") ?? "";
  const clinic_ids = queryParams.get("clinic_ids") ?? "";

  const { execute: fetchCurrentShift, closureData: shift } = useDayClosingCurrentShift();

  useEffect(() => {
    mappedClinicId && fetchCurrentShift(mappedClinicId);
  }, [mappedClinicId]);

  useEffect(() => {
    if (shiftTiming && clinic_ids && time) {
      fetchDayClosingData(clinic_ids, shiftTiming);
    } else if (shift?.shift === "day" || (shift?.shift === "night" && mappedClinicId)) {
      fetchDayClosingData(mappedClinicId, shift?.shift);
    }
  }, [mappedClinicId, time, shift?.shift, clinic_ids]);

  // RBAC: ACCOUNTING_CASH_CLOSING_CENTRAL_VIEW — legacy semantics: everyone EXCEPT
  // clinic_ops / clinic_ops_doggyworld.
  const isCentralOpps = useFeatureAccess("ACCOUNTING_CASH_CLOSING_CENTRAL_VIEW");
  const tableHeaders = ["Clinic Name", "Status", "Tasks Left"];

  return (
    <div className="w-full">
      {isCentralOpps ? (
        <>
          {isLoading ? (
            <TableSkeletonComponent columnLength={3} />
          ) : closureData?.length > 0 ? (
            <div className="scroll-inner mt-3 rounded-2xl max-h-[calc(100vh-260px)] overscroll-y-contain overflow-y-auto">
              <table className="w-full min-w-[920px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal sticky top-0 z-10">
                  <tr>
                    {tableHeaders.map((header) => (
                      <th
                        key={header}
                        scope="col"
                        className="p-3 text-left text-xs whitespace-nowrap font-medium text-black capitalize tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {closureData.map((clinic, id) => {
                    const pendingTasks = clinic.closure_data.pending || [];
                    const isCompleted = pendingTasks.length === 0;
                    const isFirst = id === 0;
                    const isLast = id === closureData.length - 1;
                    const isSingle = closureData.length === 1;

                    return (
                      <tr
                        key={clinic.clinic_id}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            p-3 text-xs font-medium text-[#0369FF] whitespace-nowrap
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {clinic.clinic_name || "-"}
                        </td>
                        <td className="p-3 text-xs whitespace-nowrap">
                          <span
                            className={`inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset ring-opacity-10 ${
                              isCompleted
                                ? "bg-[#F6FFF4] text-[#00873C] ring-[#00873C]"
                                : "bg-[#FFF5F4] text-[#CE4201] ring-[#CE4201]"
                            }`}
                          >
                            {isCompleted ? "Completed" : `${pendingTasks.length} Pending`}
                          </span>
                        </td>
                        <td
                          className={`
                            p-3 text-xs
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          {pendingTasks.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {pendingTasks.map((task, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 bg-[#F5F5F5] border border-[#E7E7E7] rounded-full text-xs text-[#1F1F23]"
                                >
                                  {task}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[#6C727F]">No pending tasks</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <NoDataFoundComponent imageURL={NO_APPOINTMENTS} message="No Clinic Closures Found" />
          )}
        </>
      ) : (
        <>
          {isLoading ? (
            <TableSkeletonComponent columnLength={3} />
          ) : (
            <div className="space-y-10 mt-4">
              {closureData?.map((clinic) => (
                <div key={clinic.clinic_id} className="flex gap-6 w-full">
                  {/* Pending Section */}
                  <div className="w-1/2">
                    <div className="rounded-2xl border overflow-hidden bg-white">
                      {clinic.closure_data.pending.length > 0 ? (
                        clinic.closure_data.pending.map((item, idx) => {
                          const match = item.match(/\d+/);
                          const number = match && match[0];
                          const label = item.split(":")[0]; // text before colon

                          return (
                            <div
                              key={idx}
                              className={`flex items-center justify-between p-4 ${
                                idx !== clinic.closure_data.pending.length - 1 ? "border-b" : ""
                              }`}
                            >
                              <span className="text-blue-600 font-medium text-xs">{label}</span>
                              <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
                                {number} Pending
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <CongratulationsCard
                          metaData={closureData?.length > 0 && closureData[0]?.completion_metadata}
                        />
                      )}
                    </div>
                  </div>

                  {/* Completed Section */}
                  <div className="w-1/2">
                    <div className="rounded-2xl border overflow-hidden bg-white">
                      {clinic.closure_data.completed.length > 0 ? (
                        clinic.closure_data.completed.map((item, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-4 ${
                              idx !== clinic.closure_data.completed.length - 1 ? "border-b" : ""
                            }`}
                          >
                            <span className="text-blue-600 font-medium text-xs">{item}</span>
                            <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-xs font-medium">
                              Completed
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-400 italic p-4">No Completed Closures</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DayclosingSummaryComponent;
